import { resolveEquipmentCatalogAssetPaths } from '@zep/utils';

import type {
  BaseEquipmentItem,
  EquipmentCatalog,
  EquipmentUiSkinConfig,
} from './avatarEquipment.types';

export interface RegisterEquipmentCatalogOptions {
  /**
   * Absolute URL prefix to prepend to every relative asset path
   * (`thumbnailPath` and `layers[].resource.body`). Already-absolute paths
   * (starting with `/`, `http://`, `https://`, or `data:`) pass through
   * unchanged so callers can register pre-resolved catalogs.
   */
  basePath?: string;
}

let _catalog: EquipmentCatalog | null = null;
let _runtimeCatalog: EquipmentCatalog | null = null;
let _renderingCatalog: EquipmentCatalog | null = null;
let _catalogRevision = 0;

const _subscribers = new Set<() => void>();
const _runtimeRequestSubscribers = new Set<() => void>();
const _pendingRuntimeCatalogGrants = new Set<string>();
const _loadedRuntimeCatalogGrants = new Set<string>();
const _equipmentItemRevisions = new WeakMap<BaseEquipmentItem, number>();
let _nextEquipmentItemRevision = 1;

type RuntimeEquipmentSelection = Partial<
  Record<
    | 'attackId'
    | 'effectId'
    | 'rideId'
    | 'petId'
    | 'speechBubbleId'
    | 'nicknameSkinId',
    number | null
  >
>;

const RUNTIME_EQUIPMENT_FIELDS: Array<keyof RuntimeEquipmentSelection> = [
  'attackId',
  'effectId',
  'rideId',
  'petId',
  'speechBubbleId',
  'nicknameSkinId',
];

export function subscribeEquipmentCatalog(listener: () => void): () => void {
  _subscribers.add(listener);
  return () => {
    _subscribers.delete(listener);
  };
}

function _notifyEquipmentCatalogSubscribers(): void {
  _subscribers.forEach(listener => listener());
}

function mergeItems<T extends BaseEquipmentItem>(
  catalogItems: T[] | undefined,
  runtimeItems: T[] | undefined,
): T[] {
  if (!runtimeItems?.length) return catalogItems ?? [];
  const runtimeIds = new Set(runtimeItems.map(item => item.id));
  return [
    ...(catalogItems ?? []).filter(item => !runtimeIds.has(item.id)),
    ...runtimeItems,
  ];
}

function mergeCatalogs(
  baseCatalog: EquipmentCatalog,
  overlayCatalog: EquipmentCatalog,
): EquipmentCatalog {
  return {
    attack: mergeItems(baseCatalog.attack, overlayCatalog.attack),
    effect: mergeItems(baseCatalog.effect, overlayCatalog.effect),
    ride: mergeItems(baseCatalog.ride, overlayCatalog.ride),
    pet: mergeItems(baseCatalog.pet, overlayCatalog.pet),
    speech_bubble: mergeItems(
      baseCatalog.speech_bubble,
      overlayCatalog.speech_bubble,
    ),
    nickname_skin: mergeItems(
      baseCatalog.nickname_skin,
      overlayCatalog.nickname_skin,
    ),
  };
}

function refreshRenderingCatalog(): void {
  if (!_catalog || !_runtimeCatalog) {
    _renderingCatalog = _runtimeCatalog ?? _catalog;
    return;
  }

  _renderingCatalog = mergeCatalogs(_catalog, _runtimeCatalog);
}

function resolveCatalogAssetPaths(
  catalog: EquipmentCatalog,
  options: RegisterEquipmentCatalogOptions,
): EquipmentCatalog {
  return options.basePath
    ? resolveEquipmentCatalogAssetPaths(
        catalog,
        createCatalogAssetPathResolver(options.basePath),
      )
    : catalog;
}

function refreshEquipmentItemRevisions(catalog: EquipmentCatalog): void {
  for (const items of Object.values(catalog)) {
    for (const item of items ?? []) {
      _equipmentItemRevisions.set(item, _nextEquipmentItemRevision++);
    }
  }
}

export function registerEquipmentCatalog(
  catalog: EquipmentCatalog,
  options: RegisterEquipmentCatalogOptions = {},
): void {
  _catalog = resolveCatalogAssetPaths(catalog, options);
  refreshEquipmentItemRevisions(_catalog);
  refreshRenderingCatalog();
  _catalogRevision++;
  _notifyEquipmentCatalogSubscribers();
}

export function registerRuntimeEquipmentCatalog(
  catalog: EquipmentCatalog,
  options: RegisterEquipmentCatalogOptions = {},
): void {
  const resolvedCatalog = resolveCatalogAssetPaths(catalog, options);
  refreshEquipmentItemRevisions(resolvedCatalog);
  _runtimeCatalog = _runtimeCatalog
    ? mergeCatalogs(_runtimeCatalog, resolvedCatalog)
    : resolvedCatalog;
  refreshRenderingCatalog();
  _catalogRevision++;
  _notifyEquipmentCatalogSubscribers();
}

export function getEquipmentCatalog(): EquipmentCatalog | null {
  return _catalog;
}

export function getRenderingEquipmentCatalog(): EquipmentCatalog | null {
  return _renderingCatalog;
}

export function getEquipmentCatalogRevision(): number {
  return _catalogRevision;
}

export function getRenderingEquipmentItemRevision(
  category: keyof EquipmentCatalog,
  itemId: number,
): number {
  const item = _renderingCatalog?.[category]?.find(
    candidate => candidate.id === itemId,
  );
  if (!item) return 0;

  const existingRevision = _equipmentItemRevisions.get(item);
  if (existingRevision) return existingRevision;

  const revision = _nextEquipmentItemRevision++;
  _equipmentItemRevisions.set(item, revision);
  return revision;
}

export function requestRuntimeEquipmentItems(
  selection: RuntimeEquipmentSelection | null | undefined,
  catalogGrant: string | null | undefined,
): void {
  if (
    !selection ||
    typeof catalogGrant !== 'string' ||
    catalogGrant.length === 0 ||
    catalogGrant.length > 512
  ) {
    return;
  }

  const hasRuntimeEquipment = RUNTIME_EQUIPMENT_FIELDS.some(field => {
    const itemId = selection[field];
    return (
      typeof itemId === 'number' && Number.isSafeInteger(itemId) && itemId > 0
    );
  });
  if (
    !hasRuntimeEquipment ||
    _pendingRuntimeCatalogGrants.has(catalogGrant) ||
    _loadedRuntimeCatalogGrants.has(catalogGrant)
  )
    return;

  _pendingRuntimeCatalogGrants.add(catalogGrant);
  _runtimeRequestSubscribers.forEach(listener => listener());
}

export function getRequestedRuntimeEquipmentCatalogGrants(): string[] {
  return [..._pendingRuntimeCatalogGrants].sort();
}

export function markRuntimeEquipmentCatalogGrantsLoaded(
  grants: readonly string[],
): void {
  let changed = false;
  for (const grant of grants) {
    if (!_pendingRuntimeCatalogGrants.delete(grant)) continue;
    _loadedRuntimeCatalogGrants.add(grant);
    changed = true;
  }
  if (changed) {
    _runtimeRequestSubscribers.forEach(listener => listener());
  }
}

export function subscribeRuntimeEquipmentRequests(
  listener: () => void,
): () => void {
  _runtimeRequestSubscribers.add(listener);
  return () => {
    _runtimeRequestSubscribers.delete(listener);
  };
}

export function getEquipmentThumbnailPath(
  category: keyof EquipmentCatalog,
  itemId: number,
): string | null {
  if (!_catalog || itemId === 0) {
    return null;
  }

  const items = _catalog[category] ?? [];
  return (
    items.find((item: BaseEquipmentItem) => item.id === itemId)
      ?.thumbnailPath ?? null
  );
}

export function getEquipmentUiSkinConfig(
  category: 'speech_bubble' | 'nickname_skin',
  itemId: number,
): EquipmentUiSkinConfig | null {
  if (!_renderingCatalog || itemId === 0) {
    return null;
  }

  const items = _renderingCatalog[category] ?? [];
  return items.find(item => item.id === itemId)?.uiSkinConfig ?? null;
}

function createCatalogAssetPathResolver(basePath: string) {
  const prefix = stripTrailingSlash(basePath);
  return (path: string) => joinPath(prefix, path);
}

function joinPath(prefix: string, path: string): string {
  if (isAbsolutePath(path)) {
    return path;
  }
  return `${prefix}/${path}`;
}

function isAbsolutePath(path: string): boolean {
  return (
    path.startsWith('/') ||
    /^https?:\/\//i.test(path) ||
    path.startsWith('data:')
  );
}

function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}
